// Location: Corporate-dashboard/Container/components/dashboard/GenderDonutChart.js

import React from 'react';
import { Typography, Space, Button, Empty } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import { ExclamationOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;

const GenderDonutChart = ({ degree, genderAnalysisData, genderSelectedData, selectedFilter, degreeDataRef, setChartRangeRef, onExport, hasData }) => {
  const degreeChartData = useSelector(state => state?.corporateDashboardPage2?.degreeChartData);

  // Check if there's any data from the bar chart or any floated roles
  const hasGenderData = (degreeChartData && degreeChartData.degreeBarChart && degreeChartData.degreeBarChart.length > 0 &&
    degreeChartData.degreeBarChart.some(item => (item.degreeGenderAnalysis?.men || 0) + (item.degreeGenderAnalysis?.women || 0) + (item.degreeGenderAnalysis?.others || 0) > 0))
    || (degreeChartData && degreeChartData.degreeBarChart && degreeChartData.degreeBarChart.some(item => (item.floated || 0) > 0));

  // Check if no degree is selected or if degree is empty/null
  // Use no selection state even when there's data (total > 0) but no degree selected
  const isNoSelection = !degree || degree === 'None' || degree === '';

  // Use API data instead of hardcoded data
  const apiData = degreeChartData?.degreeBarChart || [];
  const selectedDegreeData = apiData.find(item => item.degree === degree) || { degreeGenderAnalysis: { men: 0, women: 0, others: 0 } };
  // Extract gender data from API response
  const genderData = selectedDegreeData?.degreeGenderAnalysis ;
  console.log("genderData:", genderData);
  
  const { men, women, others } = genderData;
  // Use totalCandidate from API response only (note: singular, not plural)
  const total = genderData?.totalCandidate ;  
  // Calculate percentages safely
  const menPercentage = total > 0 ? Math.round((men / total) * 100) : 0;
  const womenPercentage = total > 0 ? Math.round((women / total) * 100) : 0;
  const othersPercentage = total > 0 ? 100 - menPercentage - womenPercentage : 0;

  // Gray out colors when no selection
  const chartColors = isNoSelection ? ['#d9d9d9'] : ['rgba(13, 58, 133, 1)', 'rgba(1, 138, 190, 1)', 'rgba(145, 225, 255, 1)'];
  const hoverColors = isNoSelection ? ['#bfbfbf'] : ['rgba(13, 58, 133, 1)', 'rgba(1, 138, 190, 1)', 'rgba(145, 225, 255, 1)'];

  const chartData = {
    labels: (isNoSelection || total === 0) ? [''] : ['Men', 'Women', 'Others'],
    datasets: [{
      data: isNoSelection ? [100] : (total === 0 ? [100] : [men, women, others]), // Show full gray circle when no selection or no data
      // Use the colors from the target image
      backgroundColor: isNoSelection ? ['#d9d9d9'] : (total === 0 ? ['#d9d9d9'] : chartColors), // Gray when no data
      borderColor: '#FFFFFF', // White border
      borderWidth: 0,
      hoverBackgroundColor: isNoSelection ? ['#bfbfbf'] : (total === 0 ? ['#bfbfbf'] : hoverColors), // Gray hover when no data
      cutout: '65%',
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%', // Adjust to make the donut thinner like the image
    plugins: {
      legend: { display: false }, // Hide default legend
      tooltip: {
        callbacks: {
          // Show number with commas in tooltip, but show 0 when no selection or no data
          label: (context) => {
            if (isNoSelection || total === 0) {
              return `: 0`;
            }
            return `${context.label}: ${context.parsed.toLocaleString()}`;
          }, 
        },
      },
      datalabels: {
      display: false,  // 👈 This line removes the labels inside the donut
    },

    },
  };
  const corporateData = useSelector((state) => state?.auth?.corporateData)

  return (
    // Main container uses flexbox column layout
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}> {/* Add gap */}

      {/* Top Row: Title on left, Export button on right */}
      <div style={{
        display: "flex",
        alignItems: "center",
        marginBottom: 8,
        gap: 8,
      }}>
        <Title level={5} style={{ margin: 0, color: '#000', fontSize: '20px' }}>
          Gender Analysis {degree && "- "}{degree && (
            <span style={{ color: "rgba(52, 62, 89, 1)", fontFamily: 'Plus Jakarta Sans', weight: '400' }}>{degree}</span>
          )}
        </Title>
        {hasGenderData && (
          <Button 
            type="default"
            onClick={onExport}
            disabled={!hasData}
            style={{
              borderColor: "#00a878",
              color: "#00a878",
              fontWeight: 500,
              borderRadius: 6,
              height: 30,
              marginLeft: "auto",
            }}
          >
            Export
          </Button>
        )}
      </div>

      {hasGenderData ? (
        <>
          {/* Middle Row: Chart on left (Legend on right) */}
          <div style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 40,
            marginTop: 40,
            flexWrap: "wrap",
          }}>
            {/* Donut Chart */}
            <div style={{ 
              position: "relative", 
              width: 280, 
              height: 280, 
              marginLeft: 80,
              transition: "all 0.3s ease",  }}>
              <Doughnut data={chartData} options={chartOptions} />
              {/* Text positioned in the center */}
              <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center', lineHeight: 1.2
              }}>
              </div>
            </div>

            {/* Info Labels */}
            {degree && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                  justifyContent: "center",
                }}
              >
                {/* Total */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 12, height: 12 }}></div>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <Text type="secondary" style={{ fontSize: 20 , fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)' }}>Total</Text>
                    <Text strong style={{ fontSize: 24, lineHeight: 1 }}>{total.toLocaleString()}</Text>
                  </div>
                </div>
                {/* Men */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "rgba(13, 58, 133, 1)",
                    }}
                  ></div>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <Text strong style={{fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Male</Text>
                    <Text strong style={{ fontSize: 18 }}>{menPercentage}%</Text>
                  </div>
                </div>
                {/* Women */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "rgba(1, 138, 190, 1)",
                    }}
                  ></div>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <Text strong style={{ fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Female</Text>
                    <Text strong style={{ fontSize: 18 }}>{womenPercentage}%</Text>
                  </div>
                </div>
                {/* Others */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: "rgba(145, 225, 255, 1)",
                    }}
                  ></div>
                  <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <Text strong style={{ fontSize: 20,fontWeight: '500', fontFamily: 'Plus Jakarta Sans', color:'rgba(0, 0, 0, 1)'}}>Others</Text>
                    <Text strong style={{ fontSize: 18 }}>{othersPercentage}%</Text>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Note when all counts are zero */}
          {degree && total === 0 && (
            <div style={{
              position: 'absolute',
              bottom: '70px',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '8px 12px',
              backgroundColor: '#f6f6f6ff',
              border: '1px solid #cc0000ff',
              borderRadius: '6px',
              textAlign: 'center',
              zIndex: 1
            }}>
              <Text style={{ 
                fontSize: 14, 
                color: '#d42727ffff',
                fontFamily: 'Plus Jakarta Sans',
                whiteSpace: 'nowrap'
              }}>
                Note: No candidates with this degree have applied.
              </Text>
            </div>
          )}

          {/* Bottom Section: "Selected" Box (sticks to the bottom) */}
          <div
            style={{
              marginTop: degree ? 120 : 100,
              backgroundColor: "#fff",
              border: degree ? "1px solid #435fe8" : "1px solid rgba(194, 194, 194, 1)",
              borderRadius: '8px',
              padding: '12px 16px',
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {degree ? (
              <>
                <Text strong type="secondary" style={{ fontSize: 14, display: 'flex-start' }}>
                  Selected
                </Text>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: "rgba(13, 58, 133, 1)",
                      borderRadius: "50%",
                    }}
                  ></div>
                  <Text style={{ fontSize: 13 }}>
                    <strong>Male:</strong> {(men || 0).toLocaleString()}
                  </Text>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: "rgba(1, 138, 190, 1)",
                      borderRadius: "50%",
                    }}
                  ></div>
                  <Text style={{ fontSize: 13 }}>
                    <strong>Female:</strong> {(women || 0).toLocaleString()}
                  </Text>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: "rgba(145, 225, 255, 1)",
                      borderRadius: "50%",
                    }}
                  ></div>
                  <Text style={{ fontSize: 13 }}>
                    <strong>Others:</strong> {(others || 0).toLocaleString()}
                  </Text>
                </div>
              </>
            ) : (
              <Space size={8}>
                <svg width="18" height="21" viewBox="0 0 18 21" style={{ marginLeft: '2px' }}>
                  {/* Line for exclamation mark */}
                  <rect 
                    x="10.05" 
                    y="3" 
                    width="4.022172927856445" 
                    height="12" 
                    fill="rgba(50, 73, 215, 1)"
                    opacity="1"
                  />
                  {/* Dot for exclamation mark */}
                  <rect 
                    x="10.05" 
                    y="17" 
                    width="4.022172927856445" 
                    height="4" 
                    fill="rgba(50, 73, 215, 1)"
                    opacity="1"
                  />
                </svg>
                <Text style={{ 
                  color: '#262626',
                  fontFamily: 'Plus Jakarta Sans',
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: '18px',
                  letterSpacing: '0%'
                }}>
                  Note: Click the graph for more details.
                </Text>
              </Space>
            )}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexGrow: 1 }}>
            <Empty />
          </div>
        )}
    </div>
  );
};

export default GenderDonutChart;
